import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Download, Network, Plus, RefreshCw, Search, X } from 'lucide-react';
import { apiClient } from '../api';
import { useAssistantRecord } from '../contexts/ChatContext';
import { errMessage, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle } from '../components/UI/ink';
import { parseTs } from '../components/SecurityAlerts/model';
import AddAssetDrawer from '../components/NetworkMap/AddAssetDrawer';
import AssetPane, { IMP_WORD, LEVEL, SEEN, isNetworkDevice } from '../components/Assets/AssetPane';

// Which machines need you, and why. The health strip filters the list, a row
// opens the docked pane (selection in ?asset=), and Registered / Archived
// switches lifecycle. Importance (1–10) is set by people; Risk (0–100) is
// calculated by /api/dashboard/risk.
const ONLINE_MS = 30 * 60 * 1000;
const DAY_MS = 86400000;
const seenState = (last) => {
  const d = parseTs(last);
  if (!d) return 'never';
  const diff = Date.now() - d.getTime();
  return diff < ONLINE_MS ? 'online' : diff < DAY_MS ? 'stale' : 'offline';
};
const agoShort = (iso) => {
  const d = parseTs(iso);
  if (!d) return '—';
  const m = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
  if (m < 60) return `${Math.max(m, 1)} min`;
  if (m < 1440) return `${Math.floor(m / 60)} h`;
  return `${Math.floor(m / 1440)} d`;
};
const TYPE = (s) => {
  if (isNetworkDevice(s)) return 'network';
  const t = String(s.device_type || '').toLowerCase();
  if (t === 'workstation' || t === 'endpoint') return 'workstation';
  if (t === 'iot') return 'iot';
  return 'server';
};
const TYPE_LABEL = { server: 'Servers', workstation: 'Workstations', network: 'Network devices', iot: 'IoT' };
const kindLabel = (s) => {
  const t = TYPE(s);
  if (t === 'network') return 'NETWORK DEVICE';
  if (t === 'iot') return 'IOT DEVICE';
  const os = /win/i.test(`${s.os_type} ${s.os_info}`) ? 'WINDOWS' : /mac|darwin/i.test(`${s.os_type} ${s.os_info}`) ? 'MACOS' : /linux|ubuntu|debian|centos|rhel|fedora/i.test(`${s.os_type} ${s.os_info}`) ? 'LINUX' : '';
  return `${os ? `${os} ` : ''}${t === 'workstation' ? 'WORKSTATION' : 'SERVER'}`;
};

const STRIP = [
  ['attn', 'NEEDS ATTENTION', 'risk 50 or more', 'var(--sev-high-fg)', 'transparent'],
  ['online', 'REPORTING', 'last 30 min', 'var(--sev-good-fg)', 'transparent'],
  ['stale', 'STALE', 'quiet 30 min – 24 h', 'var(--sev-med-fg)', 'transparent'],
  ['offline', 'NOT REPORTING', 'over 24 h or never', 'transparent', 'var(--ink-faint)'],
  ['inet', 'INTERNET-FACING', 'exposed', 'transparent', 'var(--ink-muted)'],
];
const FILTER_LABEL = { all: 'ASSETS', attn: 'NEED ATTENTION', online: 'REPORTING', stale: 'STALE', offline: 'NOT REPORTING', inet: 'INTERNET-FACING' };
const selectStyle = { background: 'var(--surf-surface)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' };

const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

export default function MyAssets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [servers, setServers] = useState([]);
  const [risk, setRisk] = useState({});
  const [baselines, setBaselines] = useState({});
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [view, setView] = useState('registered');
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [type, setType] = useState('all');
  const [loc, setLoc] = useState('all');
  const [asc, setAsc] = useState(false);
  const [adding, setAdding] = useState(false);

  const selectedId = searchParams.get('asset');
  const select = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('asset', id); else next.delete('asset');
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    const [reg, arch, rk, bl] = await Promise.allSettled([
      apiClient.get('/api/servers?state=REGISTERED'),
      apiClient.get('/api/servers?state=ARCHIVED'),
      apiClient.get('/api/dashboard/risk'),
      apiClient.get('/api/baselines'),
    ]);
    if (reg.status === 'fulfilled') {
      setServers([...(reg.value.data?.servers || []), ...(arch.status === 'fulfilled' ? arch.value.data?.servers || [] : [])]);
    } else {
      setNotice({ kind: 'error', text: errMessage(reg.reason, 'Could not load assets.') });
    }
    if (rk.status === 'fulfilled') setRisk(Object.fromEntries((rk.value.data?.servers || []).map(s => [s.id, s])));
    if (bl.status === 'fulfilled') setBaselines(Object.fromEntries((bl.value.data?.baselines || []).map(b => [b.server_id, b])));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Only REGISTERED/ARCHIVED rows are fetched, so unregistered ARP finds never
  // reach here. A device registered from the network map keeps source
  // 'arp_passive' and must still be listed.
  const assets = useMemo(() => servers
    .map(s => {
      const r = risk[s.id] || {};
      return {
        id: s.id,
        name: s.hostname || s.id,
        ip: s.ip_address || '',
        os: s.os_info || s.os_type || (isNetworkDevice(s) ? 'Network device' : ''),
        os_info: s.os_info, os_type: s.os_type, device_type: s.device_type,
        type: TYPE(s), kindLabel: kindLabel(s),
        lifecycle: s.lifecycle_state || 'REGISTERED',
        importance: s.criticality_score ?? r.criticality_score ?? 5,
        inet: !!Number(s.is_internet_facing),
        location: s.location || '',
        last_seen: s.last_seen,
        state: seenState(s.last_seen),
        risk: r.risk_score ?? 0,
        breakdown: r.breakdown || null,
        f: r.active_finding_count ?? 0,
        ioc: r.active_ioc_count ?? 0,
        cve: r.active_cve_count ?? 0,
      };
    }), [servers, risk]);

  const registered = assets.filter(a => a.lifecycle !== 'ARCHIVED');
  const archived = assets.filter(a => a.lifecycle === 'ARCHIVED');
  const passes = (a) => filter === 'all' ? true
    : filter === 'attn' ? a.risk >= 50
      : filter === 'inet' ? a.inet
        : filter === 'offline' ? a.state === 'offline' || a.state === 'never'
          : a.state === filter;
  const counts = Object.fromEntries(STRIP.map(([id]) => [id, registered.filter(a => {
    if (id === 'attn') return a.risk >= 50;
    if (id === 'inet') return a.inet;
    if (id === 'offline') return a.state === 'offline' || a.state === 'never';
    return a.state === id;
  }).length]));
  const locations = [...new Set(assets.map(a => a.location).filter(Boolean))].sort();
  const types = [...new Set(assets.map(a => a.type))];
  const needle = q.trim().toLowerCase();
  const rows = (view === 'archived' ? archived : registered.filter(passes))
    .filter(a => type === 'all' || a.type === type)
    .filter(a => loc === 'all' || a.location === loc)
    .filter(a => !needle || [a.name, a.ip, a.os].some(v => v && v.toLowerCase().includes(needle)))
    .sort((x, y) => (asc ? x.risk - y.risk : y.risk - x.risk) || x.name.localeCompare(y.name));

  const selected = assets.find(a => a.id === selectedId) || null;
  useAssistantRecord(selected && { kind: 'asset', id: selected.id, label: selected.name });

  const update = async (id, patch) => {
    try {
      await apiClient.put(`/api/servers/${id}`, patch);
      setServers(list => list.map(s => (s.id === id ? { ...s, ...patch } : s)));
      if ('criticality_score' in patch) apiClient.get('/api/dashboard/risk').then(r => setRisk(Object.fromEntries((r.data?.servers || []).map(s => [s.id, s])))).catch(() => {});
      return true;
    } catch {
      return false;
    }
  };

  const lifecycle = async (id, state) => {
    await apiClient.put(`/api/servers/${id}`, { lifecycle_state: state });
    const name = assets.find(a => a.id === id)?.name || 'Asset';
    if (state === 'DISCOVERED') {
      setServers(list => list.filter(s => s.id !== id));
      select(null);
      setNotice({ kind: 'ok', text: `${name} moved back to Discovered. It’s on the Network Map.` });
    } else {
      setServers(list => list.map(s => (s.id === id ? { ...s, lifecycle_state: state } : s)));
      setView(state === 'ARCHIVED' ? 'archived' : 'registered');
      setNotice({ kind: 'ok', text: state === 'ARCHIVED' ? `${name} archived.` : `${name} restored to My Assets.` });
    }
  };

  const exportCsv = () => {
    const head = ['hostname', 'ip', 'os', 'type', 'lifecycle', 'importance', 'risk', 'open_findings', 'open_ioc', 'open_cve', 'internet_facing', 'location', 'last_seen'];
    const lines = [head.join(','), ...rows.map(a => [a.name, a.ip, a.os, a.type, a.lifecycle, a.importance, Math.round(a.risk), a.f, a.ioc, a.cve, a.inet ? 'yes' : 'no', a.location, a.last_seen].map(csvCell).join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `assets-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const summary = loading && !servers.length ? 'Loading…' : [
    `${registered.length} registered`,
    `${counts.online} reporting`,
    `${counts.attn} need${counts.attn === 1 ? 's' : ''} attention`,
    `${counts.inet} internet-facing`,
  ].join(' · ');

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-4" style={{ color: 'var(--ink-text)' }}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[11.5px] tracking-[0.14em]" style={{ color: 'var(--ink-link)' }}>ASSETS &amp; DEFENSE</span>
          <h1 className="m-0 font-heading text-[30px] font-semibold tracking-[-0.01em] leading-tight">My Assets</h1>
          <p className="m-0 text-sm" style={{ color: 'var(--ink-muted)' }}>{summary}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button type="button" onClick={load} disabled={loading} aria-label="Refresh" className="w-11 h-11 flex items-center justify-center rounded-[10px] border disabled:opacity-60" style={secondaryBtnStyle}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.8} />
          </button>
          <Link to="/network-map" className={secondaryBtn} style={secondaryBtnStyle}><Network className="w-4 h-4" strokeWidth={1.8} />Network Map</Link>
          <button type="button" onClick={exportCsv} disabled={!rows.length} className={secondaryBtn} style={secondaryBtnStyle}><Download className="w-4 h-4" strokeWidth={1.8} />Export CSV</button>
          <button type="button" onClick={() => setAdding(true)} className={primaryBtn} style={primaryBtnStyle}><Plus className="w-4 h-4" strokeWidth={2} />Add asset</button>
        </div>
      </div>

      {notice && (
        <div role={notice.kind === 'error' ? 'alert' : 'status'} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-[10px] border text-[13px]" style={notice.kind === 'error'
          ? { background: 'var(--sev-crit-bg)', borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)' }
          : { background: 'var(--sev-good-bg)', borderColor: 'var(--sev-good-fg)' }}>
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Health strip — doubles as the filter */}
      <div role="group" aria-label="Asset health" className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
        {STRIP.map(([id, k, sub, mk, mkB]) => {
          const on = filter === id && view === 'registered';
          return (
            <button key={id} type="button" onClick={() => { setFilter(on ? 'all' : id); setView('registered'); }} aria-pressed={on} className="h-[70px] box-border flex flex-col justify-center gap-[5px] px-4 rounded-xl border text-left" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>
              <span className="flex items-center gap-[7px] font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>
                <span className="w-[7px] h-[7px] box-border rounded-sm" style={{ background: mk, border: `1px solid ${mkB}` }} />{k}
              </span>
              <span className="flex items-baseline gap-2 min-w-0">
                <span className="font-heading text-2xl font-semibold leading-none">{loading && !servers.length ? '—' : counts[id]}</span>
                <span className="text-xs truncate" style={{ color: 'var(--ink-faint)' }}>{sub}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Lifecycle" className="h-10 box-border flex p-[3px] rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          {[['registered', 'Registered', registered.length], ['archived', 'Archived', archived.length]].map(([id, label, n]) => {
            const on = view === id;
            return (
              <button key={id} type="button" onClick={() => { setView(id); setFilter('all'); }} aria-pressed={on} className="h-8 flex items-center gap-1.5 px-3 rounded-[7px] text-[13px] font-medium" style={{ background: on ? 'var(--surf-raised)' : 'transparent', color: on ? 'var(--ink-text)' : 'var(--ink-muted)' }}>
                {label}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{n}</span>
              </button>
            );
          })}
        </div>
        <div className="flex-[0_1_260px] min-w-[180px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <Search className="w-[15px] h-[15px] shrink-0" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
          <input type="search" aria-label="Search assets" placeholder="Hostname, IP or OS" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
        </div>
        <div className="flex flex-wrap gap-2 md:ml-auto">
          <label htmlFor="f-type" className="sr-only">Type</label>
          <select id="f-type" value={type} onChange={e => setType(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px]" style={selectStyle}>
            <option value="all">All types</option>
            {types.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
          {locations.length > 0 && (
            <>
              <label htmlFor="f-loc" className="sr-only">Location</label>
              <select id="f-loc" value={loc} onChange={e => setLoc(e.target.value)} className="h-10 box-border px-2.5 rounded-[10px] border text-[13px] max-w-[220px]" style={selectStyle}>
                <option value="all">All locations</option>
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_440px] gap-5 items-start">
        <section aria-label="Assets" className="rounded-[14px] border overflow-hidden min-w-0" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line)' }}>
          <div className="overflow-x-auto">
            <div className="min-w-[620px]">
              <div className="h-[42px] box-border flex items-center gap-3.5 px-4 border-b font-mono text-[10.5px] tracking-[0.12em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-faint)' }}>
                <span className="flex-grow">{rows.length} {view === 'archived' ? 'ARCHIVED' : FILTER_LABEL[filter]}</span>
                <span className="w-[84px] shrink-0">IMPORTANCE</span>
                <button type="button" onClick={() => setAsc(v => !v)} aria-label={`Sort by risk, ${asc ? 'lowest' : 'highest'} first`} className="w-[112px] shrink-0 flex items-center gap-1 font-mono text-[10.5px] tracking-[0.12em]" style={{ color: 'var(--ink-text)' }}>
                  RISK{asc ? <ChevronUp className="w-2.5 h-2.5" strokeWidth={2.4} /> : <ChevronDown className="w-2.5 h-2.5" strokeWidth={2.4} />}
                </button>
                <span className="w-[132px] shrink-0">OPEN</span>
                <span className="w-[52px] shrink-0 text-right">SEEN</span>
              </div>
              {!rows.length && (
                <div className="py-12 px-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
                  {loading ? 'Loading assets…'
                    : view === 'archived' ? 'Nothing archived.'
                      : registered.length ? 'No assets match these filters.'
                        : <>No registered assets yet. Register devices from the <Link to="/network-map" style={{ color: 'var(--ink-link)' }}>Network Map</Link>, install the <Link to="/agents" style={{ color: 'var(--ink-link)' }}>agent</Link>, or add one by hand.</>}
                </div>
              )}
              {rows.map(a => {
                const on = a.id === selectedId;
                const isArch = a.lifecycle === 'ARCHIVED';
                const sn = SEEN[isArch ? 'archived' : a.state];
                const lv = LEVEL(a.risk);
                return (
                  <div key={a.id} className="relative">
                    <button type="button" onClick={() => select(on ? null : a.id)} aria-pressed={on} className="w-full h-16 box-border flex items-center gap-3.5 px-4 border-b text-left" style={{ borderColor: 'var(--surf-line)', background: on ? 'var(--surf-raised)' : 'transparent' }}>
                      <span className="flex-grow min-w-0 flex items-center gap-[11px]">
                        <span title={sn.word} className="w-2 h-2 shrink-0 rounded-full box-border" style={{ background: sn.dot, border: `1px solid ${sn.border}` }} />
                        <span className="min-w-0 flex flex-col gap-1">
                          <span className="flex items-center gap-[7px] min-w-0">
                            <span className="text-sm font-medium truncate">{a.name}</span>
                            {a.inet && <span title="Internet-facing" className="h-[18px] shrink-0 inline-flex items-center px-[5px] rounded-[5px] border font-mono text-[9.5px] tracking-[0.06em]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>PUBLIC</span>}
                          </span>
                          <span className="font-mono text-[11.5px] truncate" style={{ color: 'var(--ink-faint)' }}>{[a.ip, a.os].filter(Boolean).join(' · ') || '—'}</span>
                        </span>
                      </span>
                      <span className="w-[84px] shrink-0 flex items-baseline gap-[5px]"><span className="font-heading text-[15px] font-semibold">{a.importance}</span><span className="text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{IMP_WORD(a.importance)}</span></span>
                      <span className="w-[112px] shrink-0 flex items-center gap-2">
                        <span className="font-mono text-[13px] font-medium w-6 text-right">{isArch ? '—' : Math.round(a.risk)}</span>
                        <span className="flex-grow h-[5px] rounded-sm overflow-hidden" style={{ background: 'var(--surf-line)' }}>
                          {!isArch && <span className="block h-[5px]" style={{ width: `${Math.min(100, a.risk)}%`, background: `var(--sev-${lv}-fg)` }} />}
                        </span>
                      </span>
                      <span className="w-[132px] shrink-0 flex gap-2.5 font-mono text-[11.5px]">
                        {isArch ? <span style={{ color: 'var(--ink-faint)' }}>–</span> : (
                          <>
                            <span style={{ color: a.f ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{a.f} F</span>
                            <span style={{ color: a.ioc ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{a.ioc} IoC</span>
                            <span style={{ color: a.cve ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{a.cve} CVE</span>
                          </>
                        )}
                      </span>
                      <span className="w-[52px] shrink-0 text-right font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{isArch ? '' : agoShort(a.last_seen)}</span>
                    </button>
                    {on && <span aria-hidden="true" className="absolute right-0 top-5 w-px h-6" style={{ background: 'var(--accent)' }} />}
                  </div>
                );
              })}
              <div className="px-4 py-3 text-xs" style={{ color: 'var(--ink-faint)' }}>F = open findings. Risk is 0–100, calculated. Importance is 1–10, set by you.</div>
            </div>
          </div>
        </section>

        <div className="xl:sticky xl:top-6 min-w-0">
          <AssetPane
            asset={selected}
            baseline={selected ? baselines[selected.id] : null}
            onClose={() => select(null)}
            onUpdate={update}
            onLifecycle={lifecycle}
          />
        </div>
      </div>

      <AddAssetDrawer open={adding} onClose={() => setAdding(false)} onAdded={load} />
    </div>
  );
}
